import React, { useEffect, useState } from "react";
import api from "../../../api/axiosConfig";
import "../../../center admin css/LabReportsEntry.css";
import "../../../center admin css/centerAdmin.css";

interface LabReportsEntryActivePatientCardProps {
  patient?: {
    emrId?: string;
    name?: string;
    genderShort?: "M" | "F";
    ageYears?: number;
    avatarUrl?: string;
  };
}

const LabReportsEntryActivePatientCard: React.FC<
  LabReportsEntryActivePatientCardProps
> = ({ patient }) => {
  const [displayUrl, setDisplayUrl] = useState<string | undefined>();

  useEffect(() => {
    const fetchSignedUrl = async () => {
      setDisplayUrl(undefined);

      // No active patient = no image
      if (!patient || !patient.avatarUrl) {
        return;
      }

      const profileKey = patient.avatarUrl;

      try {
        const token = sessionStorage.getItem("accessToken");

        if (!token) {
          console.warn("No access token found");
          return;
        }

        // Already a presigned URL
        if (
          profileKey.includes("X-Amz-Signature") ||
          profileKey.includes("X-Amz-Credential")
        ) {
          setDisplayUrl(profileKey);
          return;
        }

        // Remove leading slash if present
        const cleanPath = profileKey.startsWith("/")
          ? profileKey.slice(1)
          : profileKey;

        const parts = cleanPath.split("/");

        if (parts.length < 3) {
          console.warn("Invalid Lab Report profile key:", profileKey);
          return;
        }

        // Example:
        // checkin/capture-photo/P-2026-0000123-8/P-2026-0000123-8_capture-photo.jpg
        //
        // module = checkin/capture-photo
        // key    = P-2026-0000123-8/P-2026-0000123-8_capture-photo.jpg

        const module = `${parts[0]}/${parts[1]}`;
        const key = parts.slice(2).join("/");

        const response = await api.S3config.get("/view-url", {
          params: {
            module,
            key,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const signedUrl =
          response.data?.url || (response as any).url;

        if (signedUrl) {
          setDisplayUrl(signedUrl);
        }
      } catch (err) {
        console.error(
          "Failed to fetch presigned URL for LabReportsEntryActivePatientCard:",
          err,
        );
      }
    };

    fetchSignedUrl();
  }, [patient?.avatarUrl, patient?.emrId]);

  const genderText =
    patient?.genderShort === "M"
      ? "Male"
      : patient?.genderShort === "F"
        ? "Female"
        : "-";

  const ageText =
    typeof patient?.ageYears === "number"
      ? `${patient.ageYears} Years`
      : "-";

  if (!patient) {
    return null;
  }

  return (
    <div className="patient-details-card">
      <div className="patient-details-top">
        <div
          className="patient-avatar-large"
          style={{ overflow: "hidden" }}
        >
          {displayUrl ? (
            <img
              src={displayUrl}
              alt="Patient"
              className="patient-avatar-img"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <div className="patient-avatar-fallback" />
          )}
        </div>

        <div className="patient-info-group">
          <div className="patient-info-row">
            <div>
              <span className="field-label">EMR ID</span>
              <div className="field-value">
                {patient.emrId ?? "-"}
              </div>
            </div>

            <div>
              <span className="field-label">Name</span>
              <div className="field-value">
                {patient.name ?? "-"}
              </div>
            </div>
          </div>

          <div className="patient-info-row">
            <div>
              <span className="field-label">Age</span>
              <div className="field-value">
                {ageText}
              </div>
            </div>

            <div>
              <span className="field-label">Gender</span>
              <div className="field-value">
                {genderText}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabReportsEntryActivePatientCard;